import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import React, { useRef, useCallback, useEffect, useState } from 'react';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import { useNavigate } from 'react-router-dom';
import MetierListData from './metierListData';
import { PersistedFixedSizeList as FixedSizeList } from "../../hooks/listNavigation";
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
    name: 500, description: 800
};

const MetierList = React.memo(({ scrollRef, metiers, sortBy, setSortBy, setOffset, filteredMetiersCount, setMetiersFetched }) => {

    const navigate = useNavigate();

    const [metierColumnWidth, setMetierColumnWidth] = useState(() => {
        const storedWidths = JSON.parse(localStorage.getItem('metierColumnWidth')) || {};
        return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
    });

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // Update window height on resize
    useEffect(() => {
        const handleResize = debounce(() => {
            setWindowHeight(window.innerHeight);
        }, 300); // 300ms debounce delay

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.cancel(); // Cancel any pending debounced calls
        };
    }, []);

    const tableHeadRef = useRef(null);

    const handleColumnWidthChange = (value, columnKey) => {
        setMetierColumnWidth({ ...metierColumnWidth, [columnKey]: value })
    }

    useEffect(() => {
        localStorage.setItem('metierColumnWidth', JSON.stringify(metierColumnWidth));
    }, [metierColumnWidth]);

    // synchronize horizontal scroll between table head and list
    useSyncHorizontalScroll(tableHeadRef, scrollRef);

    // Calculate fetched items on mount
    useCalculateFetchedItems(filteredMetiersCount, setMetiersFetched, 70, 280);

    // Handle scroll and fetch more items
    const handleScrollList = useScrollFetchData(setOffset, setMetiersFetched, filteredMetiersCount, 70, 280);

    const handleMetierClick = useCallback((metierId) => navigate(`metier_details/${metierId}`), [navigate]);

    const handleSort = (field) => {
        setSortBy((prevSortBy) => {
            const nextSortOrder = prevSortBy[field] === null ? 'asc'
                : prevSortBy[field] === 'asc' ? 'desc'
                    : null;

            return Object.fromEntries(
                Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
            );
        });

    };

    const Row = ({ index, style, data }) => {
        const { metiers, handleMetierClick } = data;
        const metier = index === 0 ? metiers[0] : metiers[index];
        return (
            <Box key={index} style={{ ...style, width: 'fit-content' }}>
                <MetierListData
                    metier={metier}
                    columnWidth={metierColumnWidth}
                    handleMetierClick={handleMetierClick}
                />
            </Box>
        );
    };

    return (
        <Box className='table'>
            <Box className='tableHead' ref={tableHeadRef}>
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label='Nom'
                    columnKey='name'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    isFixed={true}
                    columnWidth={metierColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter'
                    label='Description'
                    columnKey='description'
                    sortBy={sortBy}
                    handleSort={handleSort}
                    sortable={true}
                    isFilter={false}
                    columnWidth={metierColumnWidth}
                    onColumnWidthChange={handleColumnWidthChange}
                />
                <CustomHeaderCell
                    className='table_header_cell_non_filter' width="50px" />
            </Box>
            <Box className='tableBody'>
                <FixedSizeList
                    ref={scrollRef}
                    height={Math.min(
                        filteredMetiersCount * 70 + 15,
                        windowHeight - 285
                    )}
                    width='100%'
                    itemCount={filteredMetiersCount}
                    itemSize={70}
                    itemData={{ metiers, handleMetierClick }}
                    onScroll={handleScrollList}
                >
                    {Row}
                </FixedSizeList>
            </Box>
        </Box>

    );
});

export default MetierList;